'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowRight, Cloud, Server, Check } from 'lucide-react';
import { SiteNav } from '@/components/SiteNav';
import { Reveal, MarketingFooter } from '@/components/marketing';

// La scelta prima dell'accesso. Le due formule non portano allo stesso posto:
// l'abbonamento e' un servizio che si attiva da soli (signup, prova, carta), la
// licenza e' software da installare sul server del cliente e richiede che
// interveniamo noi, quindi porta a una richiesta e non a un pagamento.
// Volere questa scelta prima della registrazione e' una decisione di Filippo:
// chi vuole il software non deve ritrovarsi con un account che non gli serve.

export default function StartContent() {
  const t = useTranslations('Start');

  const OPTIONS = [
    {
      k: 'sub' as const,
      Icon: Cloud,
      href: '/signup' as const,
      primary: true,
      points: ['subP1', 'subP2', 'subP3'] as const,
    },
    {
      k: 'lic' as const,
      Icon: Server,
      href: '/licence' as const,
      primary: false,
      points: ['licP1', 'licP2', 'licP3'] as const,
    },
  ];

  return (
    <div className="flex flex-col bg-stone-50 dark:bg-zinc-950 text-stone-950 dark:text-zinc-50">
      <SiteNav />

      <section style={{ padding: 'clamp(56px,8vw,104px) 0 clamp(24px,3vw,40px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
              style={{ color: 'var(--accent)' }}
            >
              {t('eyebrow')}
            </div>
            <h1
              className="font-black tracking-[-0.035em] leading-[1.05] m-0 mb-5 max-w-[20ch]"
              style={{ fontSize: 'clamp(38px,5.5vw,64px)' }}
            >
              {t('title')}
            </h1>
            <p className="text-[18px] leading-[1.6] text-stone-500 dark:text-zinc-400 max-w-[56ch] m-0">
              {t('body')}
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: '0 0 clamp(56px,8vw,104px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
            {OPTIONS.map(({ k, Icon, href, primary, points }) => (
              <Reveal key={k}>
                <div
                  className="h-full rounded-[18px] border bg-white dark:bg-zinc-900 p-7 flex flex-col"
                  style={{
                    borderColor: primary
                      ? 'color-mix(in srgb, var(--accent) 40%, transparent)'
                      : undefined,
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-[10px] flex items-center justify-center mb-5"
                    style={{ background: 'var(--accent-soft)' }}
                  >
                    <Icon size={19} style={{ color: 'var(--accent)' }} />
                  </div>

                  <h2 className="text-[21px] font-bold tracking-tight m-0 mb-1.5">{t(`${k}Title`)}</h2>
                  <div className="text-[15px] font-semibold mb-3" style={{ color: 'var(--accent)' }}>
                    {t(`${k}Price`)}
                  </div>
                  <p className="text-[14px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0 mb-5">
                    {t(`${k}Body`)}
                  </p>

                  <ul className="flex flex-col gap-2.5 m-0 p-0 list-none mb-7">
                    {points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-[14px] leading-[1.5]">
                        <Check size={15} className="mt-[3px] shrink-0" style={{ color: 'var(--accent)' }} />
                        <span className="text-stone-600 dark:text-zinc-300">{t(p)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    <Link
                      href={href}
                      className={
                        primary
                          ? 'inline-flex items-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity'
                          : 'inline-flex items-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] border border-stone-300 dark:border-zinc-700 hover:border-stone-400 dark:hover:border-zinc-600 transition-colors'
                      }
                    >
                      {t(`${k}Cta`)} <ArrowRight size={15} />
                    </Link>
                    <p className="text-[12.5px] text-stone-400 dark:text-zinc-500 mt-3 m-0">
                      {t(`${k}Note`)}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="text-[13px] text-stone-400 dark:text-zinc-500 mt-6 m-0">
              {t('help')}{' '}
              <Link href="/pricing" className="underline hover:text-stone-600 dark:hover:text-zinc-300">
                {t('helpLink')}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
